import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { base44 } from '@/api/base44Client';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Download, Upload, AlertTriangle, ShieldCheck, Database, RefreshCw, FileSpreadsheet, Activity, Bug } from 'lucide-react';
import * as XLSX from 'xlsx';
import { toast } from 'sonner';
import { sendDiscordReport } from '@/lib/discordAsa';
import { useSession } from '@/lib/SessionContext';

const SHEET_CONFIGS = {
  // ... (keep exact same config object)
  "Botol": [
    { header: "Nama", key: "nama" },
    { header: "Ukuran Label", key: "ukuran_label_ml" },
    { header: "Ukuran Aktual", key: "ukuran_aktual_ml" },
    { header: "Harga Beli", key: "harga_beli" },
    { header: "Harga Jual", key: "harga_jual" },
    { header: "Sisa Stok", key: "stok_pcs" },
    { header: "PO", key: "waktu_po_hari" },
    { header: "Catatan", key: "catatan" }
  ],
  "Tutup": [
    { header: "Nama", key: "nama" },
    { header: "Harga Beli", key: "harga_beli" },
    { header: "Harga Jual", key: "harga_jual" },
    { header: "Sisa Stok", key: "stok_pcs" },
    { header: "PO", key: "waktu_po_hari" },
    { header: "Catatan", key: "catatan" }
  ],
  "Spray": [
    { header: "Nama", key: "nama" },
    { header: "Harga Beli", key: "harga_beli" },
    { header: "Harga Jual", key: "harga_jual" },
    { header: "Sisa Stok", key: "stok_pcs" },
    { header: "PO", key: "waktu_po_hari" },
    { header: "Catatan", key: "catatan" }
  ],
  "Item Tambahan": [
    { header: "Nama", key: "nama" },
    { header: "Harga Beli", key: "harga_beli" },
    { header: "Harga Jual", key: "harga_jual" },
    { header: "Sisa Stok", key: "stok_pcs" },
    { header: "PO", key: "waktu_po_hari" },
    { header: "Catatan", key: "catatan" }
  ],
  "Bahan Cair": [
    { header: "Nama Bahan", key: "nama" },
    { header: "Vendor", key: "vendor" },
    { header: "Kategori", key: "kategori" },
    { header: "Harga Beli", key: "harga_beli" },
    { header: "Harga Jual", key: "harga_jual" },
    { header: "Sisa Stok (ml)", key: "stok_ml" },
    { header: "PO", key: "waktu_po_hari" },
    { header: "Catatan", key: "catatan" }
  ],
  "Database Bahan": [
    { header: "Nama Vendor", key: "nama_vendor" },
    { header: "Nama Brand", key: "nama_brand" },
    { header: "Nama Varian", key: "nama_varian" },
    { header: "Kategori Bahan", key: "kategori_bahan" },
    { header: "Harga Beli", key: "harga_beli" },
    { header: "Stok Minimal", key: "stok_minimal" },
    { header: "Sisa Stok", key: "stok_aktual" },
    { header: "Catatan", key: "catatan" }
  ],
  "Database Brand": [
    { header: "Nama Brand", key: "nama_brand" },
    { header: "Nama Varian", key: "nama_varian" },
    { header: "Ukuran Botol (ml)", key: "ukuran_ml" },
    { header: "Status", key: "status" },
    { header: "Catatan", key: "catatan" }
  ],
  "Bibit Sample": [
    { header: "Nama Bibit", key: "nama_bibit" },
    { header: "Vendor", key: "vendor" },
    { header: "Grade", key: "grade" },
    { header: "Harga", key: "harga" },
    { header: "Mata Uang", key: "mata_uang" },
    { header: "Status", key: "status_ketersediaan" },
    { header: "Catatan", key: "catatan" }
  ]
};

export default function WorkspaceBackup() {
  const { member } = useSession();
  const [isExporting, setIsExporting] = useState(false);
  const [exportProgress, setExportProgress] = useState('');
  const [isImporting, setIsImporting] = useState(false);
  const [isUnderAttack, setIsUnderAttack] = useState(false); // Simulasi Ancaman
  
  // Data Fetching for Export...
  const { data: teamMembers = [] } = useQuery({ queryKey: ["backup-team"], queryFn: () => base44.entities.TeamMember.list() });
  const { data: clients = [] } = useQuery({ queryKey: ["backup-clients"], queryFn: () => base44.entities.ClientAccount.list() });
  const { data: botol = [] } = useQuery({ queryKey: ["backup-botol"], queryFn: () => base44.entities.Botol.list() });
  const { data: tutup = [] } = useQuery({ queryKey: ["backup-tutup"], queryFn: () => base44.entities.Tutup.list() });
  const { data: spray = [] } = useQuery({ queryKey: ["backup-spray"], queryFn: () => base44.entities.Spray.list() });
  const { data: bahanCair = [] } = useQuery({ queryKey: ["backup-bahan-cair"], queryFn: () => base44.entities.BahanCair.list() });
  const { data: dbBahan = [] } = useQuery({ queryKey: ["backup-db-bahan"], queryFn: () => base44.entities.DatabaseBahan.list() });
  const { data: itemTambahan = [] } = useQuery({ queryKey: ["backup-item-tambahan"], queryFn: () => base44.entities.ItemTambahan.list() });
  const { data: racikan = [] } = useQuery({ queryKey: ["backup-racikan"], queryFn: () => base44.entities.RacikanDatabase.list() });
  const { data: bibitSample = [] } = useQuery({ queryKey: ["backup-bibit-sample"], queryFn: () => base44.entities.BibitSample.list() });

  const sleep = (ms) => new Promise(r => setTimeout(r, ms));

  const [readyBackup, setReadyBackup] = useState(null);

  const handleExport = async () => {
    setIsExporting(true);
    setExportProgress("Menyiapkan data...");
    setReadyBackup(null);
    
    await sleep(50);
    
    try {
      const wb = XLSX.utils.book_new();

      const addSheet = (data, sheetName) => {
        try {
          if (!data || data.length === 0) {
            XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet([{ Info: "Belum ada data di sistem" }]), sheetName);
            return;
          }
          
          const config = SHEET_CONFIGS[sheetName];
          
          const cleanData = data.map(item => {
            const row = {};
            
            // 1. Gunakan Template Cantik jika tersedia
            if (config) {
              config.forEach(col => {
                row[col.header] = item[col.key] !== undefined && item[col.key] !== null ? item[col.key] : "";
              });
              // Khusus Racikan
              if (sheetName === 'Database Brand' && Array.isArray(item.racikan)) {
                row['Info Racikan (Read-Only)'] = item.racikan.map(r => `${r.nama_bahan || r.kategori_bahan} (${r.persentase}%)`).join(', ');
              }
            } else {
              // Fallback untuk sheet tanpa template
              for (const key in item) {
                if (['image_url', 'foto', 'image', 'gambar', 'avatar'].includes(key)) continue;
                if (typeof item[key] === 'string' && item[key].startsWith('data:image')) continue;
                
                if (key === 'racikan' && Array.isArray(item[key])) {
                  row['Info Racikan (Read-Only)'] = item[key].map(r => `${r.nama_bahan || r.kategori_bahan} (${r.persentase}%)`).join(', ');
                } else if (item[key] !== null && typeof item[key] !== 'object') {
                  row[key] = item[key];
                }
              }
            }
            
            // 2. Sisipkan Kolom JSON_DATA di paling kanan untuk Two-Way Sync
            const baseObj = { ...item };
            ['image_url', 'foto', 'image', 'gambar', 'avatar'].forEach(k => delete baseObj[k]); // Jangan simpan gambar di JSON
            row['JSON_DATA'] = JSON.stringify(baseObj);
            
            return row;
          });
          const ws = XLSX.utils.json_to_sheet(cleanData);
          XLSX.utils.book_append_sheet(wb, ws, sheetName);
        } catch (err) {
          console.error(`Gagal memproses sheet ${sheetName}:`, err);
        }
      };

      setExportProgress("Menyusun file Excel (proses ini memakan waktu beberapa detik)...");
      
      // Proses secara sinkron
      addSheet(teamMembers, "Tim");
      addSheet(clients, "Klien");
      addSheet(botol, "Botol");
      addSheet(tutup, "Tutup");
      addSheet(spray, "Spray");
      addSheet(bahanCair, "Bahan Cair");
      addSheet(dbBahan, "Database Bahan");
      addSheet(itemTambahan, "Item Tambahan");
      addSheet(racikan, "Database Brand");
      addSheet(bibitSample, "Bibit Sample");

      if (wb.SheetNames.length === 0) {
        XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet([{ Info: "Tidak ada data untuk dibackup" }]), "Info");
      }

      const now = new Date();
      const bulan = now.toLocaleString('id-ID', { month: 'long' }).toUpperCase();
      const tgl = now.getDate().toString().padStart(2, '0');
      const jam = now.getHours().toString().padStart(2, '0') + '.' + now.getMinutes().toString().padStart(2, '0');
      const fileName = `BACKUP-WEB-${bulan}-${tgl}-${jam}.xlsx`;

      setReadyBackup({ wb, fileName });
      toast.success("Data berhasil disiapkan untuk diunduh!");
    } catch (error) {
      console.error(error);
      toast.error("Gagal melakukan export data. Silakan coba lagi.");
    } finally {
      setIsExporting(false);
      setExportProgress('');
    }
  };

  const handleDownloadReady = async () => {
    if (!readyBackup) return;
    
    try {
      // 1. Convert workbook ke tipe array buffer
      const excelBuffer = XLSX.write(readyBackup.wb, { bookType: 'xlsx', type: 'array' });
      
      // 2. Buat Blob data Excel
      const data = new Blob([excelBuffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      
      // 3. Buat URL sementara untuk file
      const url = window.URL.createObjectURL(data);
      
      // 4. Buat elemen <a> tak kasat mata lalu pancing klik
      const link = document.createElement("a");
      link.href = url;
      link.download = readyBackup.fileName;
      document.body.appendChild(link);
      link.click();
      
      // 5. Bersihkan sisa elemen dan memori
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      // Update last backup date in database (CompanySettings) for ASA AI
      try {
        const settings = await base44.entities.CompanySettings.list();
        if (settings.length > 0) {
          await base44.entities.CompanySettings.update(settings[0].id, { last_backup_date: new Date().toISOString() });
        } else {
          await base44.entities.CompanySettings.create({ last_backup_date: new Date().toISOString() });
        }
      } catch (e) { console.error('Failed to save backup date to DB:', e); }
      
      toast.success("File backup berhasil disimpan!");
      setReadyBackup(null); // Reset setelah unduh
    } catch (error) {
      console.error("Download error:", error);
      toast.error("Gagal menyimpan file. Coba gunakan browser yang berbeda.");
    }
  };

  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!window.confirm("PERINGATAN: Impor data akan menimpa/menambah baris ke database berdasarkan file Excel Anda. Lanjutkan?")) {
      e.target.value = '';
      return;
    }

    setIsImporting(true);
    try {
      const data = await file.arrayBuffer();
      const wb = XLSX.read(data);

      const restoreSheet = async (sheetName, entity) => {
        const ws = wb.Sheets[sheetName];
        if (!ws) return;
        
        const jsonData = XLSX.utils.sheet_to_json(ws);
        if (jsonData.length === 0) return;
        if (jsonData.length === 1 && jsonData[0].Info) return; // Skip placeholder sheet kosong

        console.log(`Memulihkan ${jsonData.length} baris ke ${sheetName}...`);
        
        const config = SHEET_CONFIGS[sheetName];
        
        for (let i = 0; i < jsonData.length; i++) {
          const row = jsonData[i];
          
          // Jika tidak ada JSON_DATA, kita tidak bisa melakukan sync yang aman
          if (!row['JSON_DATA']) continue;
          
          let baseData = {};
          try {
             baseData = JSON.parse(row['JSON_DATA']);
          } catch(err) {
             console.error("Gagal parse JSON_DATA", err);
             continue; // Skip baris ini
          }
          
          if (config) {
             // 1. Two-Way Sync Logic: Timpa baseData dengan data cantik dari Excel
             for (const col of config) {
                // Developer Error Logging: Cek jika user iseng ngubah header
                if (!(col.header in row)) {
                   throw new Error(`Developer Error: Judul kolom '${col.header}' tidak ditemukan di sheet '${sheetName}'. Apakah judulnya diubah? Mohon kembalikan ke format template asli.`);
                }
                
                let excelValue = row[col.header];
                
                // Pembersihan otomatis: Jika field di database adalah angka tapi di excel diisi "3 hari" atau "Rp 5000"
                if (typeof excelValue === 'string' && (col.key.includes('harga') || col.key.includes('stok') || col.key.includes('waktu') || col.key.includes('ukuran') || col.key.includes('persentase') || col.key.includes('volume'))) {
                   const numMatch = excelValue.match(/\d+/g); // Ambil angka saja
                   if (numMatch) excelValue = Number(numMatch.join(''));
                }
                
                baseData[col.key] = excelValue;
             }
          }
          
          // 2. Simpan ke database menggunakan baseData yang sudah disync
          const exist = await entity.get(baseData.id);
          if (exist) {
            await entity.update(baseData.id, baseData);
          } else {
            await entity.create(baseData);
          }
        }
      };

      await restoreSheet("Tim", base44.entities.TeamMember);
      await restoreSheet("Klien", base44.entities.ClientAccount);
      await restoreSheet("Botol", base44.entities.Botol);
      await restoreSheet("Tutup", base44.entities.Tutup);
      await restoreSheet("Spray", base44.entities.Spray);
      await restoreSheet("Bahan Cair", base44.entities.BahanCair);
      await restoreSheet("Database Bahan", base44.entities.DatabaseBahan);
      await restoreSheet("Item Tambahan", base44.entities.ItemTambahan);
      await restoreSheet("Database Brand", base44.entities.RacikanDatabase); // Ganti dari Racikan jadi Database Brand sesuai nama sheet export
      await restoreSheet("Bibit Sample", base44.entities.BibitSample);

      toast.success("Pemulihan data selesai!");
      setTimeout(() => window.location.reload(), 2000);
      
    } catch (error) {
      console.error(error);
      if (error.message.includes("Developer Error")) {
        toast.error(error.message, { duration: 10000 }); // Tampilkan error 10 detik agar kebaca
      } else {
        toast.error("Gagal melakukan impor data. Pastikan format Excel sesuai.");
      }
    } finally {
      setIsImporting(false);
      e.target.value = '';
    }
  };

  const simulateCyberAttack = async () => {
    setIsUnderAttack(true);
    toast.error("PERINGATAN: Anomali sistem terdeteksi! Mengaktifkan protokol darurat ASA.");
    
    // Kirim notifikasi gawat darurat ke Discord
    await sendDiscordReport(
      'WORKSPACE', 
      '🚨 ANCAMAN KEAMANAN TERDETEKSI (SIMULASI) 🚨', 
      `**ASA THREAT PREDICTION SYSTEM**\n\nSistem mendeteksi lonjakan trafik yang mencurigakan (kemungkinan serangan Brute-Force) pada *port 443*.\n\n**Tindakan yang direkomendasikan:**\nSegera lakukan **BACKUP DATA** sekarang juga sebelum server terpaksa di-*shutdown* dalam 30 menit ke depan untuk mencegah pencurian data.\n\n*(Ini adalah pesan simulasi yang dijalankan oleh ${member?.full_name || 'Admin'})*`, 
      '#FF0000', 
      member
    );
  };

  return (
    <div className="space-y-8 pb-16 text-[#302621] max-w-4xl mx-auto">
      <div>
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-black text-slate-800 tracking-tight">Pusat Backup & Restore</h1>
            <p className="text-sm text-slate-500 mt-2">Amankan data sistem dengan mengekspor sebagian besar database ke dalam bentuk file Excel (.xlsx), atau pulihkan database yang terhapus dengan mengunggah file backup.</p>
          </div>
          <Button variant="outline" size="sm" onClick={simulateCyberAttack} className="text-red-500 border-red-200 hover:bg-red-50">
            <Bug className="w-4 h-4 mr-2" /> Simulasi Serangan Cyber
          </Button>
        </div>
      </div>

      {isUnderAttack && (
        <div className="bg-red-600 text-white p-5 rounded-2xl shadow-xl animate-in slide-in-from-top-4 flex items-start gap-4">
          <AlertTriangle className="w-10 h-10 shrink-0 text-red-200 animate-pulse" />
          <div>
            <h3 className="font-bold text-lg mb-1 flex items-center gap-2">
              🚨 PERINGATAN ASA: SISTEM DALAM BAHAYA!
            </h3>
            <p className="text-red-100 text-sm leading-relaxed mb-3">
              Saya mendeteksi adanya aktivitas mencurigakan yang berpotensi merusak database (Brute-Force Attack). Server mungkin akan <b>DOWN</b> atau terpaksa dimatikan dalam waktu dekat. 
            </p>
            <div className="bg-red-900/40 p-3 rounded-xl border border-red-500 text-xs font-medium inline-block">
              ⚠️ Notifikasi darurat telah dikirim ke Tim IT di Discord. Mohon <b>SEGERA LAKUKAN BACKUP SEKARANG JUGA!</b>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        
        {/* Export Card */}
        <Card className="border-[#E6D5B8]/50 shadow-sm bg-white rounded-2xl overflow-hidden relative group">
          <div className="absolute top-0 right-0 p-6 opacity-5 group-hover:opacity-10 transition-opacity">
            <Download className="w-32 h-32 text-blue-600" />
          </div>
          <CardHeader className="relative z-10 pb-4">
            <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center text-blue-600 mb-4 border border-blue-100">
              <FileSpreadsheet className="w-5 h-5" />
            </div>
            <CardTitle className="text-xl font-black">Ekspor Data (Backup)</CardTitle>
            <CardDescription className="text-xs font-medium leading-relaxed mt-2">
              Sistem akan merangkum tabel berikut ke dalam satu file Excel multi-sheet. Lengkap dengan sub-kategori, ukuran, hingga harga.
            </CardDescription>
          </CardHeader>
          <CardContent className="relative z-10">
            <div className="space-y-3 mb-6">
              <div className="flex items-center gap-2 text-xs font-medium text-slate-600">
                <Database className="w-4 h-4 text-emerald-500" /> Stock & Inventori Lengkap
              </div>
              <div className="flex items-center gap-2 text-xs font-medium text-slate-600">
                <ShieldCheck className="w-4 h-4 text-emerald-500" /> Database Brand (Racikan & Bahan)
              </div>
              <div className="flex items-center gap-2 text-xs font-medium text-slate-600">
                <ShieldCheck className="w-4 h-4 text-emerald-500" /> Manajemen Tim & Klien Terverifikasi
              </div>
              <div className="flex items-center gap-2 text-xs font-medium text-slate-600">
                <Activity className="w-4 h-4 text-emerald-500" /> Data Bibit Sample
              </div>
            </div>
            
            <div className="space-y-3">
              {readyBackup ? (
                <div className="animate-in fade-in slide-in-from-bottom-2 space-y-2">
                  <div className="bg-emerald-50 border border-emerald-200 text-emerald-700 p-3 rounded-xl flex items-center justify-between gap-3">
                    <div className="text-xs font-semibold truncate flex-1">
                      {readyBackup.fileName}
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button 
                      onClick={handleDownloadReady}
                      className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white font-bold gap-2 h-11 rounded-xl shadow-sm"
                    >
                      <Download className="w-4 h-4" />
                      Simpan File Excel
                    </Button>
                    <Button 
                      variant="outline"
                      onClick={() => setReadyBackup(null)}
                      className="w-11 h-11 rounded-xl p-0 shrink-0 text-muted-foreground hover:bg-muted"
                    >
                      <RefreshCw className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ) : (
                <>
                  <Button 
                    onClick={handleExport} 
                    disabled={isExporting}
                    className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold gap-2 h-11 rounded-xl transition-all"
                  >
                    {isExporting ? <RefreshCw className="w-4 h-4 animate-spin" /> : <FileSpreadsheet className="w-4 h-4" />}
                    {isExporting ? 'Memproses Ekspor...' : 'Siapkan File Excel Sekarang'}
                  </Button>
                  
                  {isExporting && exportProgress && (
                    <div className="text-center text-xs font-medium text-blue-600 animate-pulse">
                      {exportProgress}
                    </div>
                  )}
                </>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Import Card */}
        <Card className="border-[#E6D5B8]/50 shadow-sm bg-white rounded-2xl overflow-hidden relative group">
          <div className="absolute top-0 right-0 p-6 opacity-5 group-hover:opacity-10 transition-opacity">
            <Upload className="w-32 h-32 text-amber-600" />
          </div>
          <CardHeader className="relative z-10 pb-4">
            <div className="w-10 h-10 rounded-xl bg-amber-50 flex items-center justify-center text-amber-600 mb-4 border border-amber-100">
              <RefreshCw className="w-5 h-5" />
            </div>
            <CardTitle className="text-xl font-black">Impor Data (Restore)</CardTitle>
            <CardDescription className="text-xs font-medium leading-relaxed mt-2">
              Unggah file Excel backup (.xlsx) yang pernah Anda unduh sebelumnya. Sistem akan menimpa (update) atau memasukkan (insert) baris baru sesuai isi file.
            </CardDescription>
          </CardHeader>
          <CardContent className="relative z-10">
            <div className="bg-amber-50 border border-amber-200 text-amber-800 p-3 rounded-xl flex items-start gap-3 mb-6">
              <AlertTriangle className="w-5 h-5 shrink-0 mt-0.5 text-amber-600" />
              <div className="text-[11px] leading-relaxed font-semibold">
                <strong>Hati-hati:</strong> Proses ini tidak dapat dibatalkan. Pastikan file Excel yang Anda unggah memiliki format kolom yang sama persis seperti file hasil unduhan.
              </div>
            </div>

            <div className="relative">
              <input 
                type="file" 
                accept=".xlsx, .xls"
                onChange={handleImport}
                disabled={isImporting}
                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer disabled:cursor-not-allowed z-20"
              />
              <Button 
                variant="outline"
                disabled={isImporting}
                className="w-full border-2 border-dashed border-[#D4AF37] text-[#8C6208] hover:bg-[#F9F5EC] font-bold gap-2 h-11 rounded-xl relative z-10 pointer-events-none"
              >
                {isImporting ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                {isImporting ? 'Sedang Memulihkan...' : 'Pilih File Excel Backup (.xlsx)'}
              </Button>
            </div>
          </CardContent>
        </Card>

      </div>
    </div>
  );
}
